import React, { useCallback, useState, createElement, Component } from 'react';
import { Home, ChevronDown, ChevronUp, Copy, Check, Bug } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/Button';
import { BaseErrorBoundary, ErrorFallbackProps } from '../../components/shared/BaseErrorBoundary';
import { DjayAvatar } from '../../mascot';
/**
 * PracticeErrorBoundary — Catches rendering errors in the practice session.
 *
 * Shows a friendly Djay-styled fallback UI with:
 * - "Terug naar home" navigation (doesn't re-render broken component)
 * - Collapsible error details for debugging
 * - Copy error code button
 *
 * Co-located in practice/ domain (CL-1 s63).
 */
interface PracticeErrorBoundaryProps {
  children: React.ReactNode;
  onReset: () => void;
}
/** Generates a compact error code from the error for easy sharing */
function generateErrorCode(error: Error, errorInfo: React.ErrorInfo | null): string {
  const timestamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
  const msgHash = error.message.split('').reduce((h, c) => (h << 5) - h + c.charCodeAt(0) | 0, 0).toString(36).replace('-', 'n').slice(0, 6);
  // Extract the component that crashed from the component stack
  const componentMatch = errorInfo?.componentStack?.match(/\n\s+at (\w+)/);
  const crashComponent = componentMatch?.[1] || 'Unknown';
  return `DJ-${timestamp}-${msgHash}-${crashComponent}`;
}
function ErrorDetails({
  error,
  errorInfo
}: {
  error: Error;
  errorInfo: React.ErrorInfo | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const errorCode = generateErrorCode(error, errorInfo);
  const fullErrorText = [`Error Code: ${errorCode}`, `Message: ${error.message}`, error.stack ? `\nStack:\n${error.stack}` : '', errorInfo?.componentStack ? `\nComponent Stack:${errorInfo.componentStack}` : ''].filter(Boolean).join('\n');
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullErrorText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = fullErrorText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [fullErrorText]);
  return <div className="w-full mt-6" data-id="element-8422">
      {/* Error code badge — always visible */}
      <div className="flex items-center justify-center gap-2 mb-3" data-id="element-8423">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-full text-xs font-mono text-slate-500 select-all" data-id="element-8424">
          <Bug className="w-3 h-3" data-id="element-8425" />
          {errorCode}
        </span>
        <button onClick={handleCopy} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors" title="Kopieer foutdetails" data-id="element-8426">
          
          {copied ? <>
              <Check className="w-3 h-3 text-emerald-500" data-id="element-8427" />
              <span className="text-emerald-600" data-id="element-8428">Gekopieerd!</span>
            </> : <>
              <Copy className="w-3 h-3" data-id="element-8429" />
              Kopieer
            </>}
        </button>
      </div>

      {/* Expandable details */}
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-center gap-1.5 mx-auto text-xs text-slate-400 hover:text-slate-600 transition-colors py-1" data-id="element-8430">
        
        {isOpen ? <ChevronUp className="w-3.5 h-3.5" data-id="element-8431" /> : <ChevronDown className="w-3.5 h-3.5" data-id="element-8432" />}
        {isOpen ? 'Verberg details' : 'Toon foutdetails'}
      </button>

      <AnimatePresence data-id="element-8433">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-8434">
          
            <div className="mt-3 p-4 bg-slate-900 rounded-xl text-left overflow-auto max-h-48" data-id="element-8435">
              <p className="text-[11px] font-mono text-rose-300 break-all leading-relaxed whitespace-pre-wrap" data-id="element-8436">
                {error.message}
              </p>
              {error.stack && <p className="text-[10px] font-mono text-slate-500 break-all mt-2 leading-relaxed whitespace-pre-wrap" data-id="element-8437">
                  {error.stack.split('\n').slice(1, 6).join('\n')}
                </p>}
              {errorInfo?.componentStack && <p className="text-[10px] font-mono text-slate-600 break-all mt-2 leading-relaxed whitespace-pre-wrap" data-id="element-8438">
                  {errorInfo.componentStack.split('\n').slice(0, 5).join('\n')}
                </p>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export class PracticeErrorBoundary extends Component<PracticeErrorBoundaryProps> {
  renderFallback = ({
    error,
    errorInfo
  }: ErrorFallbackProps): React.ReactNode => {
    return <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4 sm:p-6" data-id="element-8439">
        <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="max-w-md w-full" data-id="element-8440">
          
          <div className="bg-white rounded-3xl p-8 sm:p-10 text-center shadow-xl border border-slate-200/60" data-id="element-8441">
            {/* Mascot with subtle animation */}
            <motion.div animate={{
            y: [0, -4, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="mx-auto mb-6" data-id="element-8442">
              
              <DjayAvatar size="xl" emotion="thinking" data-id="element-8443" />
            </motion.div>

            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-8444">
              Oeps, dat ging mis!
            </h2>
            <p className="text-slate-500 mb-8 text-base sm:text-lg leading-relaxed font-medium" data-id="element-8445">
              Er ging iets fout bij het laden van de oefensessie. Ga terug en
              probeer het opnieuw.
            </p>

            {/* Primary action — navigate home without re-rendering broken component */}
            <Button onClick={() => {
            // Only navigate home — do NOT reset error boundary state
            // (that would re-render the broken component and crash again)
            this.props.onReset();
          }} variant="primary" size="xl" fullWidth className="shadow-lg hover:shadow-xl transition-shadow rounded-2xl" data-id="element-8446">
              
              <Home className="w-5 h-5 mr-2" data-id="element-8447" />
              Terug naar home
            </Button>

            {/* Error details — always available for debugging */}
            <ErrorDetails error={error} errorInfo={errorInfo} data-id="element-8448" />
          </div>
        </motion.div>
      </div>;
  };
  render(): React.ReactNode {
    return <BaseErrorBoundary logPrefix="Practice Session" fallback={this.renderFallback} onReset={this.props.onReset} data-id="element-8449">
        
        {this.props.children}
      </BaseErrorBoundary>;
  }
}